import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const STRIPE_LINKS = {
  monthly: 'https://buy.stripe.com/dRm5kE0BFag1fMR9SU3F604',
  lifetime: 'https://buy.stripe.com/8x29AU1FJag1dEJ4yA3F603',
};

const Pricing = () => {
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handleSelectPlan = (subscriptionType) => {
    setSelectedPlan(subscriptionType);
    setLoading(true);

    if (window.fbq) {
      window.fbq('track', 'ViewContent', {
        content_name: subscriptionType === 'monthly' ? 'Monthly Plan' : 'Lifetime Plan',
        value: subscriptionType === 'monthly' ? 19.00 : 199.00,
        currency: 'EUR'
      });
    }

    const link = STRIPE_LINKS[subscriptionType];
    if (link) {
      window.location.href = link;
    } else {
      toast.error('Zahlungslink nicht gefunden.');
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-6xl w-full">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-white mb-4">
            Wähle deinen Plan
          </h1>
          <p className="text-xl text-gray-300">
            Erstelle professionelle Werbeanzeigen mit KI-Power
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Monthly Plan */}
          <Card className="shadow-2xl border-2 border-amber-500 hover:border-amber-400 transition-all duration-300 relative bg-white">
            <CardHeader className="bg-gradient-to-br from-amber-50 to-amber-100">
              <CardTitle className="text-2xl text-center text-slate-900">Monatlich</CardTitle>
              <div className="text-center mt-4">
                <span className="text-5xl font-bold text-slate-900">19€</span>
                <span className="text-gray-600 ml-2">/Monat</span>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Unbegrenzte AdCopy-Generierung</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">KI-Bildgenerierung für Ads</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">22 Produkt-Templates</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Brand-Kit Verwaltung</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Alle Social Media Formate</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Monatlich kündbar</span>
                </li>
              </ul>
              
              <Button
                onClick={() => handleSelectPlan('monthly')}
                disabled={loading}
                className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900"
              >
                {loading && selectedPlan === 'monthly' ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Wird geladen...
                  </>
                ) : (
                  'Monatlich starten'
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Lifetime Plan */}
          <Card className="shadow-2xl border-4 border-green-500 hover:border-green-600 transition-all duration-300 relative bg-white">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg flex items-center gap-2 animate-pulse">
                <Sparkles className="w-4 h-4" />
                BELIEBT
              </span>
            </div>
            <CardHeader className="bg-gradient-to-br from-green-50 to-emerald-100 pt-8">
              <CardTitle className="text-2xl text-center text-slate-900">Lifetime</CardTitle>
              <div className="text-center mt-4">
                <span className="text-5xl font-bold text-green-600">199€</span>
                <span className="text-gray-600 ml-2 block mt-2">Einmalig</span>
              </div>
              <p className="text-center text-sm text-green-700 mt-2 font-semibold">
                Spare über 100€ im ersten Jahr!
              </p>
            </CardHeader>
            <CardContent className="pt-6">
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <span className="font-semibold text-gray-700">Alles aus Monatlich, PLUS:</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">🔥 Lebenslanger Zugang</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">🔥 Alle zukünftigen Features</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">🔥 Prioritäts-Support</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">🔥 Keine monatlichen Kosten</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">🔥 Einmalige Investition</span>
                </li>
              </ul>
              
              <Button
                onClick={() => handleSelectPlan('lifetime')}
                disabled={loading}
                className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
              >
                {loading && selectedPlan === 'lifetime' ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Wird geladen...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Lifetime sichern
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Trust Badges */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">✓ Sichere Zahlung mit Stripe</p>
          <p className="text-gray-600">✓ Sofortiger Zugang nach Zahlung</p>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
